import React, { useMemo, useState } from 'react';
import { getCsrfToken as csrfToken } from '../../../lib/csrf.js';
import { Link, router } from '@inertiajs/react';
import Layout from '../../../components/Layout.jsx';

function StatusBadge({ mapped, liveOnly }) {
  if (liveOnly) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border bg-indigo-50 text-indigo-700 border-indigo-200">
        Live only
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${
      mapped
        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
        : 'bg-rose-50 text-rose-700 border-rose-200'
    }`}>
      {mapped ? 'Mapped' : 'Unmapped'}
    </span>
  );
}

export default function LicenseMappingsIndex({ licenses = [] }) {
  const [query, setQuery] = useState('');
  const [facility, setFacility] = useState('all');
  const [showUnmappedOnly, setShowUnmappedOnly] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return licenses.filter((l) => {
      if (facility !== 'all' && l.facility_type !== facility) return false;
      if (showUnmappedOnly && (l.mapping || l.live_only)) return false;
      if (q && !l.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [licenses, query, facility, showUnmappedOnly]);

  const unmappedCount = useMemo(() => licenses.filter((l) => !l.mapping && !l.live_only).length, [licenses]);

  const handleRefresh = (e) => {
    e.preventDefault();
    router.post('/superadmin/license_mappings/refresh_catalog', {}, {
      headers: { 'X-CSRF-Token': csrfToken() },
    });
  };

  return (
    <Layout>
      <div className="px-6 py-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">License → LearnWorlds Mapping</h1>
            <p className="mt-1 text-sm text-gray-600">
              {licenses.length} licenses · <span className={unmappedCount > 0 ? 'text-rose-600 font-semibold' : ''}>{unmappedCount} unmapped</span>
            </p>
          </div>
          <form onSubmit={handleRefresh}>
            <button
              type="submit"
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer"
            >
              Refresh LW catalog
            </button>
          </form>
        </div>

        <div className="flex flex-wrap gap-3 mb-4 items-center">
          <input
            type="text"
            placeholder="Search license name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
          />
          <select
            value={facility}
            onChange={(e) => setFacility(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
          >
            <option value="all">All facilities</option>
            <option value="RCFE">RCFE</option>
            <option value="GH">GH</option>
            <option value="ARF">ARF</option>
            <option value="STRTP">STRTP</option>
          </select>
          <label className="inline-flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={showUnmappedOnly}
              onChange={(e) => setShowUnmappedOnly(e.target.checked)}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            Unmapped only
          </label>
        </div>

        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">License</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Facility</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Billing</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Mapping</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Status</th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((l) => (
                <tr key={l.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm text-gray-900">{l.name}</td>
                  <td className="px-4 py-2 text-xs font-mono text-gray-600">{l.facility_type}</td>
                  <td className="px-4 py-2 text-xs text-gray-600">{l.billing_model}</td>
                  <td className="px-4 py-2 text-xs text-gray-700 font-mono">{l.live_only ? '— (live-only)' : l.mapping_summary}</td>
                  <td className="px-4 py-2"><StatusBadge mapped={Boolean(l.mapping)} liveOnly={l.live_only} /></td>
                  <td className="px-4 py-2 text-right">
                    <Link
                      href={`/superadmin/license_mappings/${l.id}/edit`}
                      className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                    >
                      Edit
                    </Link>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">No licenses match these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
